import { create } from "zustand";
import type { NotificationType } from '../services/notification.service';

/** Per-user in-app preference map (true = show, false = suppress). */
export type InAppPrefs = Record<NotificationType, boolean>;
/** Per-user, per-type timestamp — notifications with createdAt below
 *  this value are hidden even when the pref is true. Set on OFF→ON
 *  transitions so users don't get a flood of accumulated backlog when
 *  re-enabling. `0` means "no filter". See notificationPreferences.ts
 *  for the storage shape. */
export type EnabledSince = Record<NotificationType, number>;

const DEFAULT_IN_APP_PREFS: InAppPrefs = {
  deliveryNotifications: true,
  deliveryConfirmations: true,
  deliveryRejections: true,
  reviewNotifications: true,
  accessRequests: true,
  accessRequestResults: true,
  ftpAccessRequests: true,
  ftpAccessResults: true,
};

const DEFAULT_ENABLED_SINCE: EnabledSince = {
  deliveryNotifications: 0,
  deliveryConfirmations: 0,
  deliveryRejections: 0,
  reviewNotifications: 0,
  accessRequests: 0,
  accessRequestResults: 0,
  ftpAccessRequests: 0,
  ftpAccessResults: 0,
};

/**
 * Notification store state interface
 * Tracks pending licenses and requests for the notification bell
 */
interface NotificationsState {
  // Pending items counts
  pendingLicensesCount: number;      // For clients: licenses to claim
  rejectedRequestsCount: number;     // For clients: rejected license requests
  pendingRequestsCount: number;      // For artists: incoming license requests
  expiredLicensesCount: number;      // For clients: expired/revoked licenses
  pendingReviewsCount: number;       // For clients: spots pending approval review
  reviewResultsCount: number;        // For artists: spots that have been approved/rejected
  pendingDeliveriesCount: number;    // For broadcasters: jobs delivered to them
  deliveryRejectionsCount: number;   // For studios: broadcasters that rejected deliveries
  accessRequestsCount: number;       // For source orgs: incoming access requests from broadcasters
  accessRequestResultsCount: number; // For requester orgs: approvals/denials of their access requests
  ftpAccessRequestsCount: number;    // For broadcaster orgs: incoming FTP access requests
  ftpAccessResultsCount: number;     // For studio orgs: FTP access approval/denial results

  // Modal visibility (controlled externally via bell click)
  showPendingLicensesModal: boolean;
  showPendingRequestsModal: boolean;
  showExpiredLicensesModal: boolean;
  showPendingReviewsModal: boolean;
  showReviewResultsModal: boolean;
  showDeliveryNotificationsModal: boolean;
  showDeliveryRejectionsModal: boolean;
  showAccessRequestsModal: boolean;
  showAccessRequestResultsModal: boolean;
  showFtpAccessRequestsModal: boolean;
  showFtpAccessResultsModal: boolean;

  // Actions
  setPendingLicensesCount: (count: number) => void;
  setRejectedRequestsCount: (count: number) => void;
  setPendingRequestsCount: (count: number) => void;
  setExpiredLicensesCount: (count: number) => void;
  setPendingReviewsCount: (count: number) => void;
  setReviewResultsCount: (count: number) => void;
  setPendingDeliveriesCount: (count: number) => void;
  setDeliveryRejectionsCount: (count: number) => void;
  setAccessRequestsCount: (count: number) => void;
  setAccessRequestResultsCount: (count: number) => void;
  setFtpAccessRequestsCount: (count: number) => void;
  setFtpAccessResultsCount: (count: number) => void;
  setShowPendingLicensesModal: (show: boolean) => void;
  setShowPendingRequestsModal: (show: boolean) => void;
  setShowExpiredLicensesModal: (show: boolean) => void;
  setShowPendingReviewsModal: (show: boolean) => void;
  setShowReviewResultsModal: (show: boolean) => void;
  setShowDeliveryNotificationsModal: (show: boolean) => void;
  setShowDeliveryRejectionsModal: (show: boolean) => void;
  setShowAccessRequestsModal: (show: boolean) => void;
  setShowAccessRequestResultsModal: (show: boolean) => void;
  setShowFtpAccessRequestsModal: (show: boolean) => void;
  setShowFtpAccessResultsModal: (show: boolean) => void;
  openNotifications: () => void;
  openNextNotification: () => void;
  clearCounts: () => void;

  /** Per-user in-app preferences. Defaults all true; refreshed when the
   *  active org changes (loaded from the membership doc). When a type is
   *  false, its count is excluded from the bell badge and its modal is
   *  excluded from the open-on-click queue. */
  inAppPrefs: InAppPrefs;
  setInAppPrefs: (prefs: Partial<InAppPrefs>) => void;
  resetInAppPrefs: () => void;

  /** Per-user, per-type "enabled since" timestamps. Subscribers filter
   *  their notification lists against this so re-enabled types don't
   *  flood with backlog. See notificationPreferences.ts. */
  inAppEnabledSince: EnabledSince;
  setInAppEnabledSince: (values: Partial<EnabledSince>) => void;
  resetInAppEnabledSince: () => void;

  // Internal queue for staggered modals
  _notificationQueue: string[];

  // Transient page banner — set by an action, displayed by a top-level component,
  // auto-dismisses after the given TTL (default 10s).
  actionBanner: ActionBanner | null;
  showActionBanner: (banner: ActionBanner) => void;
  dismissActionBanner: () => void;

  // Computed
  getTotalCount: () => number;
}

export interface ActionBanner {
  variant: 'success' | 'warning' | 'error' | 'info';
  title?: string;
  message?: string;
  items?: string[];
  autoDismissMs?: number;
}

/**
 * Notifications store using Zustand
 * Manages pending license/request counts and modal visibility
 */
export const useNotificationsStore = create<NotificationsState>()((set, get) => ({
  // Initial state
  pendingLicensesCount: 0,
  rejectedRequestsCount: 0,
  pendingRequestsCount: 0,
  expiredLicensesCount: 0,
  pendingReviewsCount: 0,
  reviewResultsCount: 0,
  pendingDeliveriesCount: 0,
  deliveryRejectionsCount: 0,
  accessRequestsCount: 0,
  accessRequestResultsCount: 0,
  ftpAccessRequestsCount: 0,
  ftpAccessResultsCount: 0,
  showPendingLicensesModal: false,
  showPendingRequestsModal: false,
  showExpiredLicensesModal: false,
  showPendingReviewsModal: false,
  showReviewResultsModal: false,
  showDeliveryNotificationsModal: false,
  showDeliveryRejectionsModal: false,
  showAccessRequestsModal: false,
  showAccessRequestResultsModal: false,
  showFtpAccessRequestsModal: false,
  showFtpAccessResultsModal: false,
  _notificationQueue: [],
  actionBanner: null,
  inAppPrefs: { ...DEFAULT_IN_APP_PREFS },
  inAppEnabledSince: { ...DEFAULT_ENABLED_SINCE },

  setInAppPrefs: (prefs) => set((state) => ({ inAppPrefs: { ...state.inAppPrefs, ...prefs } })),
  resetInAppPrefs: () => set({ inAppPrefs: { ...DEFAULT_IN_APP_PREFS } }),
  setInAppEnabledSince: (values) => set((state) => ({ inAppEnabledSince: { ...state.inAppEnabledSince, ...values } })),
  resetInAppEnabledSince: () => set({ inAppEnabledSince: { ...DEFAULT_ENABLED_SINCE } }),

  showActionBanner: (banner) => set({ actionBanner: banner }),
  dismissActionBanner: () => set({ actionBanner: null }),

  // Set pending licenses count (for clients)
  setPendingLicensesCount: (count) => set({ pendingLicensesCount: count }),

  // Set rejected requests count (for clients)
  setRejectedRequestsCount: (count) => set({ rejectedRequestsCount: count }),

  // Set pending requests count (for artists)
  setPendingRequestsCount: (count) => set({ pendingRequestsCount: count }),

  // Set expired licenses count (for clients)
  setExpiredLicensesCount: (count) => set({ expiredLicensesCount: count }),

  // Set pending reviews count (for clients)
  setPendingReviewsCount: (count) => set({ pendingReviewsCount: count }),

  // Set review results count (for artists)
  setReviewResultsCount: (count) => set({ reviewResultsCount: count }),

  // Set pending deliveries count (for broadcasters)
  setPendingDeliveriesCount: (count) => set({ pendingDeliveriesCount: count }),
  setDeliveryRejectionsCount: (count) => set({ deliveryRejectionsCount: count }),
  setAccessRequestsCount: (count) => set({ accessRequestsCount: count }),
  setAccessRequestResultsCount: (count) => set({ accessRequestResultsCount: count }),
  setFtpAccessRequestsCount: (count) => set({ ftpAccessRequestsCount: count }),
  setFtpAccessResultsCount: (count) => set({ ftpAccessResultsCount: count }),

  // Control modal visibility — when closing, automatically open the next queued notification
  setShowPendingLicensesModal: (show) => { set({ showPendingLicensesModal: show }); if (!show) get().openNextNotification(); },
  setShowPendingRequestsModal: (show) => { set({ showPendingRequestsModal: show }); if (!show) get().openNextNotification(); },
  setShowExpiredLicensesModal: (show) => { set({ showExpiredLicensesModal: show }); if (!show) get().openNextNotification(); },
  setShowPendingReviewsModal: (show) => { set({ showPendingReviewsModal: show }); if (!show) get().openNextNotification(); },
  setShowReviewResultsModal: (show) => { set({ showReviewResultsModal: show }); if (!show) get().openNextNotification(); },
  setShowDeliveryNotificationsModal: (show) => { set({ showDeliveryNotificationsModal: show }); if (!show) get().openNextNotification(); },
  setShowDeliveryRejectionsModal: (show) => { set({ showDeliveryRejectionsModal: show }); if (!show) get().openNextNotification(); },
  setShowAccessRequestsModal: (show) => { set({ showAccessRequestsModal: show }); if (!show) get().openNextNotification(); },
  setShowAccessRequestResultsModal: (show) => { set({ showAccessRequestResultsModal: show }); if (!show) get().openNextNotification(); },
  setShowFtpAccessRequestsModal: (show) => { set({ showFtpAccessRequestsModal: show }); if (!show) get().openNextNotification(); },
  setShowFtpAccessResultsModal: (show) => { set({ showFtpAccessResultsModal: show }); if (!show) get().openNextNotification(); },

  // Open notifications one at a time (called from bell click).
  // Respects per-user `inAppPrefs` — types the user has disabled in-app are
  // omitted from the queue (and from getTotalCount). Legacy license-related
  // counts aren't part of the pref system and always queue when non-zero.
  openNotifications: () => {
    const state = get();
    const prefs = state.inAppPrefs;
    const queue: string[] = [];
    // Legacy license-related queues — not in the email/in-app pref system.
    if (state.pendingLicensesCount > 0 || state.rejectedRequestsCount > 0) queue.push('showPendingLicensesModal');
    if (state.pendingRequestsCount > 0) queue.push('showPendingRequestsModal');
    if (state.expiredLicensesCount > 0) queue.push('showExpiredLicensesModal');
    // Pref-gated queues. pendingReviews + reviewResults both map to the
    // `reviewNotifications` notification type.
    if (state.pendingReviewsCount > 0 && prefs.reviewNotifications) queue.push('showPendingReviewsModal');
    if (state.reviewResultsCount > 0 && prefs.reviewNotifications) queue.push('showReviewResultsModal');
    if (state.pendingDeliveriesCount > 0 && prefs.deliveryNotifications) queue.push('showDeliveryNotificationsModal');
    if (state.deliveryRejectionsCount > 0 && prefs.deliveryRejections) queue.push('showDeliveryRejectionsModal');
    if (state.accessRequestsCount > 0 && prefs.accessRequests) queue.push('showAccessRequestsModal');
    if (state.accessRequestResultsCount > 0 && prefs.accessRequestResults) queue.push('showAccessRequestResultsModal');
    if (state.ftpAccessRequestsCount > 0 && prefs.ftpAccessRequests) queue.push('showFtpAccessRequestsModal');
    if (state.ftpAccessResultsCount > 0 && prefs.ftpAccessResults) queue.push('showFtpAccessResultsModal');

    set({ _notificationQueue: queue });
    if (queue.length > 0) {
      set({ [queue[0]]: true } as any);
    }
  },

  // Called when a notification modal is dismissed — opens the next one in queue
  openNextNotification: () => {
    const state = get();
    const queue = state._notificationQueue;
    if (queue.length <= 1) {
      set({ _notificationQueue: [] });
      return;
    }
    const remaining = queue.slice(1);
    set({ _notificationQueue: remaining, [remaining[0]]: true } as any);
  },

  // Clear all counts (on org change or logout)
  clearCounts: () => set({
    pendingLicensesCount: 0,
    rejectedRequestsCount: 0,
    pendingRequestsCount: 0,
    expiredLicensesCount: 0,
    pendingReviewsCount: 0,
    reviewResultsCount: 0,
    pendingDeliveriesCount: 0,
    deliveryRejectionsCount: 0,
    accessRequestsCount: 0,
    accessRequestResultsCount: 0,
    ftpAccessRequestsCount: 0,
    ftpAccessResultsCount: 0,
  }),

  // Get total notification count. Per-user in-app prefs mask out counts for
  // types the user has disabled. Legacy license-related counts always count
  // since they're not part of the pref system.
  getTotalCount: () => {
    const state = get();
    const p = state.inAppPrefs;
    const legacyLicenseTotal =
      state.pendingLicensesCount + state.rejectedRequestsCount +
      state.pendingRequestsCount + state.expiredLicensesCount;
    const review = (p.reviewNotifications ? (state.pendingReviewsCount + state.reviewResultsCount) : 0);
    const delivery = (p.deliveryNotifications ? state.pendingDeliveriesCount : 0);
    const deliveryRej = (p.deliveryRejections ? state.deliveryRejectionsCount : 0);
    const accessReq = (p.accessRequests ? state.accessRequestsCount : 0);
    const accessRes = (p.accessRequestResults ? state.accessRequestResultsCount : 0);
    const ftpReq = (p.ftpAccessRequests ? state.ftpAccessRequestsCount : 0);
    const ftpRes = (p.ftpAccessResults ? state.ftpAccessResultsCount : 0);
    return legacyLicenseTotal + review + delivery + deliveryRej + accessReq + accessRes + ftpReq + ftpRes;
  },
}));
